import { NestedHome, TODO } from "@habity/types";
import { capitalizeAllWords } from "@habity/utils";
import { getCategoryText } from "./general";
import { getRooms } from "./rooms";

export const getStreetName = (street: string) => {
  const firstDigit = street.search(/\d/);
  const streetName =
    firstDigit && firstDigit > 0 ? street.slice(0, firstDigit).trim() : street;
  return capitalizeAllWords(streetName);
};

/**
 * Normalize special characters to their common equivalents
 * This allows searching for "alle" to match "allé" and similar cases
 */
const normalizeSpecialChars = (word: string): string => {
  if (!word) return word;

  // Character mappings for Swedish special characters
  const charMappings: { [key: string]: string } = {
    é: "e",
    è: "e",
    ê: "e",
    ë: "e",
    á: "a",
    à: "a",
    â: "a",
    ä: "a",
    å: "a",
    ö: "o",
    ô: "o",
    ò: "o",
    ó: "o",
    ü: "u",
    ù: "u",
    ú: "u",
    û: "u",
  };

  let normalized = word.toLowerCase();

  // Replace special characters with common equivalents
  Object.entries(charMappings).forEach(([special, common]) => {
    normalized = normalized.replace(new RegExp(special, "g"), common);
  });

  return normalized;
};

export const normalizeSearchTerm = (term: string): string => {
  if (!term) {
    return "";
  }

  return term.trim();
};

export const getStreetParts = (searchInput: string) => {
  const parts = searchInput.trim().split(/\s+/);

  const commonCities = [
    "stockholm",
    "göteborg",
    "malmö",
    "uppsala",
    "västerås",
    "örebro",
    "linköping",
    "helsingborg",
    "jönköping",
    "norrköping",
    "lund",
    "umeå",
    "gävle",
    "borås",
    "södertälje",
    "eskilstuna",
    "halmstad",
    "växjö",
    "karlstad",
    "sundsvall",
    "östersund",
  ];

  let streetName = "";
  let streetNumber: string | null = null;
  let city: string | null = null;

  const cityIndex = parts.findIndex((part) =>
    commonCities.includes(normalizeSpecialChars(part.toLowerCase()))
  );

  if (cityIndex !== -1) {
    city = parts[cityIndex];
    const remainingParts = [
      ...parts.slice(0, cityIndex),
      ...parts.slice(cityIndex + 1),
    ];

    // Find the LAST number in remaining parts (street numbers are at the end)
    let numberIndex = -1;
    for (let i = remainingParts.length - 1; i >= 0; i--) {
      if (/^\d+[A-Za-z]?$/.test(remainingParts[i])) {
        numberIndex = i;
        break;
      }
    }

    // Only treat as street number if it's not the first part (to handle "1 Maj Vägen")
    if (numberIndex !== -1 && numberIndex > 0) {
      streetNumber = remainingParts[numberIndex];
      
      // Check if there's a letter suffix after the number (e.g., "5 A" -> "5A")
      let suffixIndex = -1;
      if (
        numberIndex + 1 < remainingParts.length &&
        /^[A-Za-z]$/.test(remainingParts[numberIndex + 1])
      ) {
        streetNumber = streetNumber + remainingParts[numberIndex + 1];
        suffixIndex = numberIndex + 1;
      }
      
      // Build street name excluding the number and optional suffix
      streetName = remainingParts
        .filter((_, i) => i !== numberIndex && i !== suffixIndex)
        .join(" ");
    } else {
      streetName = remainingParts.join(" ");
    }
  } else {
    // Find the LAST standalone number in the parts (street numbers are typically at the end)
    // This handles streets like "1 Maj Vägen 5" where "1 Maj Vägen" is the street name and "5" is the number
    let lastNumberIndex = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (/^\d+[A-Za-z]?$/.test(parts[i])) {
        lastNumberIndex = i;
        break;
      }
    }

    const hasNumber = lastNumberIndex !== -1;
    const isNumberFirst = lastNumberIndex === 0;

    if (hasNumber && !isNumberFirst) {
      // Number found and it's not the first part - this is the street number
      streetName = parts.slice(0, lastNumberIndex).join(" ");
      streetNumber = parts[lastNumberIndex];

      // Check if there's a letter suffix after the number (e.g., "5 A" -> "5A")
      if (
        lastNumberIndex + 1 < parts.length &&
        /^[A-Za-z]$/.test(parts[lastNumberIndex + 1])
      ) {
        streetNumber = streetNumber + parts[lastNumberIndex + 1];
        city = parts.slice(lastNumberIndex + 2).join(" ") || null;
      } else {
        city = parts.slice(lastNumberIndex + 1).join(" ") || null;
      }
    } else {
      // No number found, or number is first (likely part of street name like "1 Maj Vägen")
      // The entire input is the street name (no street number detected)
      streetName = parts.join(" ");
      city = null;
    }
  }

  return {
    streetName: streetName.trim(),
    streetNumber: streetNumber ? streetNumber.toLowerCase() : null, // Normalize to lowercase
    city: city ? capitalizeAllWords(city, true) : null,
  };
};

export const getStreet = (home: NestedHome | null) => {
  if (!home) return "";

  const { status, address } = home || {};
  const { show_address } = status || {};
  const { street, city, municipality, category, rooms } = address || {};
  const location = home.address?.area?.name || city || municipality;

  if (!show_address) {
    const categoryText = getCategoryText(category);
    const roomsText = rooms ? ` på ${getRooms(rooms)}` : '';
    return `${categoryText}${roomsText}, ${location}`;
  }

  return street;
};

export const formatStreet = (street: string | null): string | null => {
  if (!street) {
    return null;
  }

  const firstDigit = street.search(/\d/);

  const streetName =
    firstDigit > 0 ? street.slice(0, firstDigit).trim() : street;
  const numberPart = firstDigit > 0 ? street.slice(firstDigit).trim() : "";

  const formattedStreetName = capitalizeAllWords(streetName, true);

  let formattedNumberPart = numberPart;

  if (formattedNumberPart) {
    formattedNumberPart = formattedNumberPart.replace(
      /(\d)([a-zåäö])/gi,
      (_, num, letter) => `${num}${letter.toUpperCase()}`
    );

    formattedNumberPart = formattedNumberPart.replace(
      /\b(\d+)\s*[Tt][Rr]\b/g,
      (_, num) => `${num} tr`
    );

    formattedNumberPart = formattedNumberPart.replace(/\bVÅN\b/g, "vån");

    formattedNumberPart = formattedNumberPart.replace(
      /\(([^)]+)\)/g,
      (_, content) => `(${content.toLowerCase()})`
    );

    formattedNumberPart = formattedNumberPart.replace(
      /(\d)\s+(?=[A-Z])/g,
      "$1"
    );
  }

  return formattedNumberPart
    ? `${formattedStreetName} ${formattedNumberPart}`
    : formattedStreetName;
};
